import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Users } from 'lucide-react';
import { SearchInput } from '@/components/ui/search-input';
import type { EmployeeTaskSummary } from '@pms/shared';
import { tasksService } from '@/services/tasks.service';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { EmployeeCard } from '@/components/tasks/EmployeeCard';
import { EmployeeTasksDrawer } from '@/components/tasks/EmployeeTasksDrawer';

export function AllEmployeesTab() {
  const [search, setSearch] = useState('');
  const [dept, setDept] = useState('all');
  const [selected, setSelected] = useState<EmployeeTaskSummary | null>(null);

  // Department options come from the task data itself. Reading /api/departments would tie
  // the Tasks module to the HRMS master_departments module, which most managers and team
  // leaders don't have — the dual permission∩module check 403s them out of the filter.
  // Keyed unfiltered so the options stay put while filtering, and so it dedupes with the
  // list query below in the default state rather than costing an extra request.
  const deptOptionsQ = useQuery({
    queryKey: ['tasks', 'by-employee', 'all', ''],
    queryFn: () => tasksService.byEmployee({}),
  });
  const departments = useMemo(() => {
    const byId = new Map<string, string>();
    for (const emp of deptOptionsQ.data ?? []) {
      if (emp.department_id && emp.department_name) byId.set(emp.department_id, emp.department_name);
    }
    return [...byId]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [deptOptionsQ.data]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['tasks', 'by-employee', dept, search],
    queryFn: () => tasksService.byEmployee({
      department_id: dept === 'all' ? undefined : dept,
      q: search.trim() || undefined,
    }),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 rounded-xl border border-border/60 bg-card p-4 sm:flex-row">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search employees…"
          containerClassName="flex-1"
        />
        <Select value={dept} onValueChange={setDept}>
          <SelectTrigger className="sm:w-56"><SelectValue placeholder="All Departments" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Departments</SelectItem>
            {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <Users className="h-4 w-4" /> {data?.length ?? 0} employees
      </p>

      {isLoading && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {[...Array(10)].map((_, i) => <div key={i} className="h-44 animate-pulse rounded-xl bg-muted/50" />)}
        </div>
      )}
      {isError && (
        <div className="rounded-xl border border-border/60 p-10 text-center text-sm text-muted-foreground">
          Couldn’t load employees. <button className="text-primary underline" onClick={() => refetch()}>Retry</button>
        </div>
      )}
      {data && data.length === 0 && (
        <div className="rounded-xl border border-dashed border-border/60 p-10 text-center text-sm text-muted-foreground">
          No employees with tasks found.
        </div>
      )}
      {data && data.length > 0 && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {data.map((emp) => <EmployeeCard key={emp.user_id} emp={emp} onClick={() => setSelected(emp)} />)}
        </div>
      )}

      <EmployeeTasksDrawer
        employee={selected}
        open={!!selected}
        onOpenChange={(o) => !o && setSelected(null)}
      />
    </div>
  );
}
